import { Link, router, usePage } from "@inertiajs/react"
import {
  CalendarRange,
  FolderKanban,
  LogOut,
  Menu,
  ReceiptText,
  ShieldCheck,
  Users,
  X,
} from "lucide-react"
import { type ReactNode, useState } from "react"
import { Logo } from "@/components/logo"
import { Button } from "@/components/ui/button"
import { Sheet } from "@/components/ui/sheet"
import { useTranslation } from "@/lib/i18n"
import { usePageProps } from "@/lib/use-page-props"
import { cn } from "@/lib/utils"

function NavItem({
  icon,
  label,
  href,
  active,
  onNavigate,
  className,
}: {
  icon: ReactNode
  label: string
  href?: string
  active?: boolean
  onNavigate?: () => void
  className?: string
}) {
  const classes = cn(
    "flex items-center gap-2.5 rounded-lg px-3 py-2 font-medium text-sm",
    active && "bg-pine-100 font-semibold text-pine-800",
    !active && href && "text-muted-foreground hover:bg-pine-50 hover:text-foreground",
    !href && "cursor-default text-muted-foreground/40",
    className,
  )

  if (!href) {
    return (
      <span className={classes} aria-disabled="true">
        {icon}
        {label}
      </span>
    )
  }

  return (
    <Link
      href={href}
      onClick={onNavigate}
      className={classes}
      aria-current={active ? "page" : undefined}
    >
      {icon}
      {label}
    </Link>
  )
}

// The nav body (links + account footer) shared by the desktop sidebar and the
// mobile drawer. `onNavigate` lets the drawer close itself when a link is tapped,
// since the admin layout persists across Inertia visits. On the drawer the items
// grow to a >=44px tap target; the desktop sidebar keeps its denser spacing.
function AdminNav({ onNavigate, drawer }: { onNavigate?: () => void; drawer?: boolean }) {
  const { t } = useTranslation()
  const { auth } = usePageProps()
  const { url } = usePage()
  const itemClass = drawer ? "min-h-11 py-2.5 text-base" : undefined

  return (
    <>
      <nav className="flex-1 space-y-1 p-3">
        <NavItem
          icon={<CalendarRange className="h-4 w-4" />}
          label={t("nav.grid")}
          href="/admin/attendance"
          active={url.startsWith("/admin/attendance")}
          onNavigate={onNavigate}
          className={itemClass}
        />
        <NavItem
          icon={<ReceiptText className="h-4 w-4" />}
          label={t("nav.payroll")}
          href="/admin/payroll"
          active={url.startsWith("/admin/payroll")}
          onNavigate={onNavigate}
          className={itemClass}
        />
        <NavItem
          icon={<Users className="h-4 w-4" />}
          label={t("nav.employees")}
          href="/admin/employees"
          active={url.startsWith("/admin/employees")}
          onNavigate={onNavigate}
          className={itemClass}
        />
        <NavItem
          icon={<FolderKanban className="h-4 w-4" />}
          label={t("nav.projects")}
          href="/admin/projects"
          active={url.startsWith("/admin/projects")}
          onNavigate={onNavigate}
          className={itemClass}
        />
        {auth.admin?.can_manage_admins && (
          <NavItem
            icon={<ShieldCheck className="h-4 w-4" />}
            label={t("nav.admins")}
            href="/admin/admins"
            active={url.startsWith("/admin/admins")}
            onNavigate={onNavigate}
            className={itemClass}
          />
        )}
      </nav>

      <div className="border-border border-t p-3">
        {auth.admin && (
          <>
            <p className="px-2 font-semibold text-sm">{auth.admin.name}</p>
            <p className="mb-2 px-2 text-muted-foreground text-xs">
              {t(`admin.roles.${auth.admin.role}`)}
            </p>
          </>
        )}
        <Button
          variant="ghost"
          className={cn("w-full justify-start", drawer && "min-h-11")}
          onClick={() => router.delete("/admin/logout")}
        >
          <LogOut className="h-4 w-4" />
          {t("session.logout")}
        </Button>
      </div>
    </>
  )
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="flex min-h-screen bg-background print:bg-card">
      {/* Desktop sidebar — unchanged at md and up, hidden on phones. */}
      <aside className="hidden w-60 flex-none flex-col border-border border-r bg-card md:flex print:hidden">
        <div className="flex h-16 items-center gap-2 border-border border-b px-4">
          <Logo className="h-8 w-8 text-graphite" />
          <span className="font-display font-bold text-pine-800 text-xl">{t("app.name")}</span>
        </div>
        <AdminNav />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar — hamburger opens the nav drawer; hidden at md and up. */}
        <header className="sticky top-0 z-30 flex h-16 flex-none items-center gap-2 border-border border-b bg-card px-3 md:hidden print:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label={t("nav.open_menu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Logo className="h-7 w-7 text-graphite" />
          <span className="font-display font-bold text-lg text-pine-800">{t("app.name")}</span>
        </header>

        <main className="min-w-0 flex-1 p-4 md:p-6 print:p-0">{children}</main>
      </div>

      {/* Nav drawer — only reachable below md; wrapped so a resize to desktop hides it. */}
      <div className="md:hidden">
        <Sheet
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          label={t("nav.menu")}
          closeLabel={t("nav.close_menu")}
        >
          <div className="flex h-16 flex-none items-center justify-between gap-2 border-border border-b px-3">
            <div className="flex items-center gap-2">
              <Logo className="h-7 w-7 text-graphite" />
              <span className="font-display font-bold text-lg text-pine-800">{t("app.name")}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={t("nav.close_menu")}
              onClick={() => setMenuOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <AdminNav drawer onNavigate={() => setMenuOpen(false)} />
        </Sheet>
      </div>
    </div>
  )
}
